import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  Table,
  Input,
  Button,
  Tag,
  Tooltip,
  Modal,
  Skeleton,
  message,
  Select,
  Checkbox,
  Spin,
  Progress,
} from 'antd'
import {
  SearchOutlined,
  MessageOutlined,
  MailOutlined,
  UserAddOutlined,
  FilterOutlined,
} from '@ant-design/icons'
import styled from 'styled-components/macro'
import { supplierPortalTheme } from 'src/branding/supplierPortalTheme'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { API } from 'src/utils/interceptor'
import { fetchAllCustomers } from 'src/features/CustomerSlice'
import Talk from 'talkjs'
import { supplierNotifications } from 'src/features/ChecklistSlice'

const CommunicateTab = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const account = useSelector(state => state.auth.account)
  const customers = useSelector(state => state.customer?.customers || [])
  const customersLoading =
    useSelector(state => state.customer?.isLoading) ?? false
  const [searchText, setSearchText] = useState('')
  const [filters, setFilters] = useState({
    active: false,
    inactive: false,
    recentlyJoined: false,
  })
  const [emailModalVisible, setEmailModalVisible] = useState(false)
  const [broadcastMessageModalVisible, setBroadcastMessageModalVisible] =
    useState(false)
  const [progressModalVisible, setProgressModalVisible] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [emailSending, setEmailSending] = useState(false)
  const [allTimeLastOrders, setAllTimeLastOrders] = useState({})
  const [venueUsers, setVenueUsers] = useState([])
  const [venueUsersLoading, setVenueUsersLoading] = useState(false)
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [bulkEmailModalVisible, setBulkEmailModalVisible] = useState(false)
  const [bulkEmailRecipients, setBulkEmailRecipients] = useState([])
  const [bulkEmailRecipientsLoading, setBulkEmailRecipientsLoading] =
    useState(false)
  const [selectedBulkEmails, setSelectedBulkEmails] = useState([])
  const [bulkEmailSubject, setBulkEmailSubject] = useState('')
  const [bulkEmailBody, setBulkEmailBody] = useState('')
  const [bulkEmailSending, setBulkEmailSending] = useState(false)
  const [selectedBroadcastVenues, setSelectedBroadcastVenues] = useState([])
  const [broadcastMessageText, setBroadcastMessageText] = useState('')
  const [broadcastSending, setBroadcastSending] = useState(false)
  const [broadcastSearchText, setBroadcastSearchText] = useState('')
  const [broadcastProgress, setBroadcastProgress] = useState({
    visible: false,
    current: 0,
    total: 0,
    success: 0,
    failed: 0,
    completed: false,
  })

  // Ref to track current TalkJS popup for cleanup
  const currentPopupRef = useRef(null)
  // Ref to track click outside handler for cleanup
  const clickOutsideHandlerRef = useRef(null)

  // Supplier IDs available to the user
  const supplierIds = useMemo(() => {
    return account?.supplier?.map(s => s._id).filter(Boolean) || []
  }, [account])

  // Use first supplier ID for single-supplier operations (e.g., sending email)
  const supplierId = supplierIds.length > 0 ? supplierIds[0] : null

  // Get supplier title for TalkJS (same pattern as checklist)
  const supplierTitle = account?.supplier?.[0]?.title || 'Supplier'

  useEffect(() => {
    if (supplierIds.length > 0) {
      dispatch(fetchAllCustomers(supplierIds))
    }
  }, [dispatch, supplierIds])

  // Cleanup: Remove click outside handler on component unmount
  useEffect(() => {
    return () => {
      if (clickOutsideHandlerRef.current) {
        document.removeEventListener('click', clickOutsideHandlerRef.current)
        clickOutsideHandlerRef.current = null
      }
      if (currentPopupRef.current) {
        try {
          currentPopupRef.current.destroy()
          currentPopupRef.current = null
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    }
  }, [])

  // Fetch all-time last orders for all customers from backend stats
  useEffect(() => {
    // Only proceed if we have account ID and it's a supplier admin
    if (!account?._id || !account?.supplierAdmin) {
      setAllTimeLastOrders({})
      return
    }

    let isCancelled = false

    const fetchAllTimeStats = async () => {
      try {
        console.log(
          '📊 Fetching supplier stats from backend (CommunicateTab)...',
        )
        const response = await API.get(
          'api2',
          `suppliers/stats?accountId=${account._id}`,
        )

        if (response?.success && response?.data) {
          console.log('✅ Stats fetched (CommunicateTab):', response.data)

          // Transform the stats data into the format expected by the UI
          const lastOrders = {}

          response.data.venueStats?.forEach(stat => {
            const venueId = stat.venue?._id || stat.venue
            if (venueId && stat.lastOrderDate) {
              // Store the most recent order date for each venue across all suppliers
              if (
                !lastOrders[venueId] ||
                new Date(stat.lastOrderDate) > new Date(lastOrders[venueId])
              ) {
                lastOrders[venueId] = stat.lastOrderDate
              }
            }
          })

          if (!isCancelled) {
            setAllTimeLastOrders(lastOrders)
            console.log(
              '📈 All-time stats applied to CommunicateTab:',
              lastOrders,
            )
          }
        } else if (response?.success && response?.data === null) {
          console.log(
            'ℹ️ No stats calculated yet for this account (CommunicateTab)',
          )
          if (!isCancelled) {
            setAllTimeLastOrders({})
          }
        } else {
          console.error(
            'Failed to fetch stats (CommunicateTab):',
            response?.message,
          )
          if (!isCancelled) {
            setAllTimeLastOrders({})
          }
        }
      } catch (error) {
        console.error('Failed to fetch all-time stats (CommunicateTab):', error)
        if (!isCancelled) {
          setAllTimeLastOrders({})
        }
      }
    }

    fetchAllTimeStats()

    return () => {
      isCancelled = true
    }
  }, [account?._id, account?.supplierAdmin])

  // Fetch venue users when email modal opens
  useEffect(() => {
    if (!emailModalVisible || !selectedCustomer?._id) {
      setVenueUsers([])
      setSelectedEmail(null)
      return
    }

    let isCancelled = false

    const fetchVenueUsers = async () => {
      setVenueUsersLoading(true)
      try {
        const response = await API.get('api', `venue/${selectedCustomer._id}`)
        const venueData = response || {}

        // Build list of users: primary account + additional users
        const users = []

        // Add primary account owner
        if (venueData.account?.emailAddress) {
          users.push({
            email: venueData.account.emailAddress,
            name: venueData.account.contactName || 'Primary owner',
            isPrimary: true,
          })
        }

        // Add additional users
        if (
          venueData.additionalUsers &&
          Array.isArray(venueData.additionalUsers)
        ) {
          venueData.additionalUsers.forEach(user => {
            if (user.emailAddress) {
              users.push({
                email: user.emailAddress,
                name: user.contactName || user.name || 'Additional user',
                isPrimary: false,
              })
            }
          })
        }

        if (!isCancelled) {
          setVenueUsers(users)
          // Auto-select first email if available
          if (users.length > 0 && !selectedEmail) {
            setSelectedEmail(users[0].email)
          }
        }
      } catch (error) {
        console.error('Failed to fetch venue users:', error)
        if (!isCancelled) {
          setVenueUsers([])
        }
      } finally {
        if (!isCancelled) {
          setVenueUsersLoading(false)
        }
      }
    }

    fetchVenueUsers()

    return () => {
      isCancelled = true
    }
  }, [emailModalVisible, selectedCustomer?._id])

  const getLastOrderDate = customer => {
    // First check enriched data, then fall back to customer data
    const venueId = customer?._id || customer?.venueId
    return (
      customer?.lastOrderDisplay ||
      (venueId ? allTimeLastOrders[venueId] : null) ||
      customer?.lastOrderDate ||
      customer?.lastOrder ||
      customer?.orderStats?.lastOrder ||
      null
    )
  }

  const getOnboardedDate = customer => {
    // First check explicit onboarded date fields
    if (customer?.onboardedDate) return customer.onboardedDate
    if (customer?.onBoarded) return customer.onBoarded
    if (customer?.createdAt) return customer.createdAt

    // Fallback: Extract creation date from MongoDB ObjectId
    if (
      customer?._id &&
      typeof customer._id === 'string' &&
      customer._id.length >= 8
    ) {
      try {
        const createdAtFromId = new Date(
          parseInt(customer._id.substring(0, 8), 16) * 1000,
        )
        if (createdAtFromId && !isNaN(createdAtFromId.getTime())) {
          return createdAtFromId.toISOString()
        }
      } catch (error) {
        // Invalid _id format, continue to return null
      }
    }

    return null
  }

  const getCustomerStatusValue = customer => {
    const lastOrderDate = getLastOrderDate(customer)
    if (lastOrderDate) {
      const parsed = moment(lastOrderDate)
      if (parsed.isValid()) {
        return parsed.isAfter(moment().subtract(30, 'days'))
          ? 'active'
          : 'inactive'
      }
    }
    const onboardedDate = getOnboardedDate(customer)
    if (onboardedDate) {
      const parsedOnboarded = moment(onboardedDate)
      if (
        parsedOnboarded.isValid() &&
        parsedOnboarded.isAfter(moment().subtract(30, 'days'))
      ) {
        return 'recent'
      }
    }
    if (typeof customer?.status === 'string' && customer.status.trim()) {
      return customer.status.trim().toLowerCase()
    }
    if (typeof customer?.isActive === 'boolean') {
      return customer.isActive ? 'active' : 'inactive'
    }
    return 'unknown'
  }

  const getCustomerStatusMeta = customer => {
    const statusValue = getCustomerStatusValue(customer)
    switch (statusValue) {
      case 'active':
        return { label: 'Active', color: '#16a34a' }
      case 'inactive':
        return { label: 'Inactive', color: '#dc2626' }
      case 'recent':
        return { label: 'Recently joined', color: '#2f80ed' }
      default:
        return { label: 'Pending', color: '#94a3b8' }
    }
  }

  // Filter and search logic
  const filteredCustomers = useMemo(() => {
    let filtered = [...customers]

    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim()
      filtered = filtered.filter(customer => {
        const venue = (customer.venueTitle || '').toLowerCase()
        const account = (customer.accountNumber || '').toLowerCase()
        return venue.includes(searchLower) || account.includes(searchLower)
      })
    }

    const activeFilters = Object.entries(filters).filter(
      ([, isActive]) => isActive,
    )
    if (activeFilters.length > 0) {
      const thirtyDaysAgo = moment().subtract(30, 'days')
      filtered = filtered.filter(customer =>
        activeFilters.some(([filterType]) => {
          if (filterType === 'active') {
            return getCustomerStatusValue(customer) === 'active'
          }
          if (filterType === 'inactive') {
            const status = getCustomerStatusValue(customer)
            return status === 'inactive' || status === 'unknown'
          }
          if (filterType === 'recentlyJoined') {
            const onboardDate = getOnboardedDate(customer)
            if (!onboardDate) return false
            const parsed = moment(onboardDate)
            return parsed.isValid() && parsed.isAfter(thirtyDaysAgo)
          }
          return false
        }),
      )
    }

    return filtered
  }, [customers, searchText, filters, allTimeLastOrders])

  // Enrich filtered customers with last order dates
  const filteredCustomersWithStats = useMemo(() => {
    return filteredCustomers.map(customer => {
      const venueId = customer?._id || customer?.venueId
      return {
        ...customer,
        lastOrderDisplay: allTimeLastOrders[venueId] || null,
      }
    })
  }, [filteredCustomers, allTimeLastOrders])

  const hasSearch = searchText.trim().length > 0
  const anyFiltersActive = Object.values(filters).some(Boolean)
  const showNoCustomersEmptyState =
    !customersLoading &&
    customers.length === 0 &&
    !hasSearch &&
    !anyFiltersActive
  const showSearchEmptyState =
    !customersLoading &&
    customers.length > 0 &&
    filteredCustomersWithStats.length === 0 &&
    hasSearch
  const showFilterEmptyState =
    !customersLoading &&
    customers.length > 0 &&
    filteredCustomersWithStats.length === 0 &&
    !hasSearch &&
    anyFiltersActive
  const showDefaultEmptyState =
    !customersLoading &&
    customers.length > 0 &&
    filteredCustomersWithStats.length === 0 &&
    !hasSearch &&
    !anyFiltersActive

  const handleFilterToggle = filterType => {
    setFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType],
    }))
  }

  const navigateToCustomers = () => {
    history.push('/supplier/customers')
  }

  const handleClearFilters = () => {
    setFilters({
      active: false,
      inactive: false,
      recentlyJoined: false,
    })
  }

  const handleMessageClick = async customer => {
    if (!supplierId || !supplierTitle) {
      message.error('Supplier information not available. Please try again.')
      return
    }

    const venueId = customer?._id || customer?.venueId
    const venueTitle = customer?.venueTitle || customer?.title || 'Customer'

    if (!venueId) {
      message.error('Customer information not available. Please try again.')
      return
    }

    try {
      await Talk.ready

      // Close previous popup if it exists
      if (currentPopupRef.current) {
        try {
          currentPopupRef.current.destroy()
          currentPopupRef.current = null
        } catch (e) {
          // Ignore errors when destroying previous popup
        }
      }

      // Remove previous click outside handler if it exists
      if (clickOutsideHandlerRef.current) {
        document.removeEventListener('click', clickOutsideHandlerRef.current)
        clickOutsideHandlerRef.current = null
      }

      const me = new Talk.User({
        id: supplierId,
        name: supplierTitle,
        welcomeMessage: 'Hey there! How are you? :-)',
        role: 'default',
      })

      const other = new Talk.User({
        id: venueId,
        name: venueTitle,
        welcomeMessage: 'Hey there! Send us a message to start a chat 😁',
        role: 'venues',
      })

      // Reuse existing session or create new one
      if (!window.talkSession) {
        window.talkSession = new Talk.Session({
          appId: 'GR4DzdvG',
          me: me,
        })
      } else {
        // Update the session's me user if needed
        window.talkSession.me = me
      }

      const conversation = window.talkSession.getOrCreateConversation(
        Talk.oneOnOneId(me, other),
      )

      conversation.setParticipant(me)
      conversation.setParticipant(other)

      const popup = window.talkSession.createPopup(conversation, {
        keepOpen: true,
      })

      // Store reference to current popup
      currentPopupRef.current = popup

      popup.onSendMessage(() => {
        // Trigger notification when message is sent
        try {
          supplierNotifications(venueId, supplierId)
        } catch (e) {
          console.error('Error sending notification:', e)
        }
      })

      popup.onClose(() => {
        // Clear reference when popup is closed
        currentPopupRef.current = null
        // Remove click outside handler
        if (clickOutsideHandlerRef.current) {
          document.removeEventListener('click', clickOutsideHandlerRef.current)
          clickOutsideHandlerRef.current = null
        }
      })

      popup.select(conversation)
      await popup.mount({ show: true })

      // Add click outside to close functionality
      // Wait a bit for TalkJS to render the popup DOM
      setTimeout(() => {
        const handleClickOutside = event => {
          if (!currentPopupRef.current) return

          // Find the TalkJS popup element in the DOM
          // TalkJS creates elements with various class patterns
          const talkjsSelectors = [
            '[class*="talkjs"]',
            '[id*="talkjs"]',
            '[class*="talk-js"]',
            '.talkjs-container',
            '.talk-popup',
            '[data-talkjs]',
            // Common TalkJS wrapper classes
            '[class*="talkjs-popup"]',
            '[class*="talkjs-chatbox"]',
          ]

          let isClickInside = false

          // Check all possible TalkJS selectors
          talkjsSelectors.forEach(selector => {
            try {
              const elements = document.querySelectorAll(selector)
              elements.forEach(element => {
                if (
                  element.contains(event.target) ||
                  element === event.target
                ) {
                  isClickInside = true
                }
              })
            } catch (e) {
              // Ignore invalid selector errors
            }
          })

          // Also check if the click target or its parents have TalkJS-related classes
          let element = event.target
          while (element && element !== document.body) {
            const className = element.className
            const id = element.id
            if (
              (typeof className === 'string' &&
                (className.includes('talkjs') ||
                  className.includes('talk-js'))) ||
              (typeof id === 'string' &&
                (id.includes('talkjs') || id.includes('talk-js')))
            ) {
              isClickInside = true
              break
            }
            element = element.parentElement
          }

          // If click is outside, close the popup
          if (!isClickInside && currentPopupRef.current) {
            try {
              currentPopupRef.current.destroy()
              currentPopupRef.current = null
              // Remove the event listener
              document.removeEventListener(
                'click',
                clickOutsideHandlerRef.current,
              )
              clickOutsideHandlerRef.current = null
            } catch (e) {
              console.error('Error closing popup on outside click:', e)
            }
          }
        }

        // Store handler reference and add event listener
        clickOutsideHandlerRef.current = handleClickOutside
        // Use capture phase to catch clicks before they bubble
        document.addEventListener('click', handleClickOutside, true)
      }, 200) // Small delay to ensure TalkJS DOM is fully rendered
    } catch (error) {
      console.error('Error opening TalkJS chat:', error)
      message.error('Failed to open chat. Please try again.')
    }
  }

  const handleEmailClick = customer => {
    setSelectedCustomer(customer)
    setEmailSubject('')
    setEmailBody('')
    setSelectedEmail(null) // Reset selected email
    setEmailModalVisible(true)
  }

  const handleSendEmail = async () => {
    if (!selectedCustomer || !supplierId) {
      message.error('Missing customer or supplier information')
      return
    }

    if (!emailSubject.trim() || !emailBody.trim()) {
      message.error('Subject and email body are required')
      return
    }

    if (!selectedEmail) {
      message.error('Please select an email address')
      return
    }

    setEmailSending(true)
    try {
      const response = await API.post(
        'api2',
        'supplier/marketing/email/send-one',
        {
          body: {
            supplierId: supplierId,
            venueId: selectedCustomer._id,
            subject: emailSubject.trim(),
            emailBody: emailBody.trim(),
            recipientEmail: selectedEmail, // Pass selected email
          },
        },
      )

      if (response.success) {
        message.success('Email sent successfully!')
        setEmailModalVisible(false)
        setSelectedCustomer(null)
        setEmailSubject('')
        setEmailBody('')
        setSelectedEmail(null) // Reset after send
      } else {
        message.error(response.error || 'Failed to send email')
      }
    } catch (error) {
      console.error('Error sending email:', error)
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to send email. Please try again.'
      message.error(errorMessage)
    } finally {
      setEmailSending(false)
    }
  }

  // Gather all emails from all customers (primary + secondary + additional users)
  const gatherAllCustomerEmails = async () => {
    setBulkEmailRecipientsLoading(true)
    try {
      const emailMap = new Map() // email -> { emailAddress, venueIds: Set, venueNames: [] }

      // Process each customer
      for (const customer of filteredCustomersWithStats) {
        const venueId = customer?._id || customer?.venueId
        if (!venueId) continue

        try {
          const venueData = customer || {}

          // Add primary account email
          if (venueData?.emailAddress) {
            const email = venueData.emailAddress
            if (!emailMap.has(email)) {
              emailMap.set(email, {
                emailAddress: email,
                venueIds: new Set(),
                venueNames: [],
              })
            }
            emailMap.get(email).venueIds.add(venueId)
            emailMap.get(email).venueNames.push({
              venueId,
              venueName: customer.venueTitle || 'Unknown Venue',
            })
          }

          // Add secondary emails from account
          if (
            venueData.account?.secondaryEmail &&
            Array.isArray(venueData.account.secondaryEmail)
          ) {
            venueData.account.secondaryEmail.forEach(secondaryEmail => {
              if (secondaryEmail) {
                if (!emailMap.has(secondaryEmail)) {
                  emailMap.set(secondaryEmail, {
                    emailAddress: secondaryEmail,
                    venueIds: new Set(),
                    venueNames: [],
                  })
                }
                emailMap.get(secondaryEmail).venueIds.add(venueId)
                emailMap.get(secondaryEmail).venueNames.push({
                  venueId,
                  venueName: customer.venueTitle || 'Unknown Venue',
                })
              }
            })
          }

          // Add additional users' emails
          if (
            venueData.additionalUsers &&
            Array.isArray(venueData.additionalUsers)
          ) {
            venueData.additionalUsers.forEach(user => {
              if (user.emailAddress) {
                if (!emailMap.has(user.emailAddress)) {
                  emailMap.set(user.emailAddress, {
                    emailAddress: user.emailAddress,
                    venueIds: new Set(),
                    venueNames: [],
                  })
                }
                emailMap.get(user.emailAddress).venueIds.add(venueId)
                emailMap.get(user.emailAddress).venueNames.push({
                  venueId,
                  venueName: customer.venueTitle || 'Unknown Venue',
                })
              }
            })
          }
        } catch (error) {
          console.error(`Failed to fetch emails for venue ${venueId}:`, error)
          // Continue with other venues even if one fails
        }
      }

      // Convert map to array format
      const recipients = Array.from(emailMap.values()).map(item => ({
        emailAddress: item.emailAddress,
        venueIds: Array.from(item.venueIds),
        venueNames: item.venueNames,
      }))

      setBulkEmailRecipients(recipients)
      // Auto-select all emails by default
      setSelectedBulkEmails(recipients.map(r => r.emailAddress))
    } catch (error) {
      console.error('Failed to gather customer emails:', error)
      message.error('Failed to load customer emails')
      setBulkEmailRecipients([])
    } finally {
      setBulkEmailRecipientsLoading(false)
    }
  }

  const handleBulkEmailClick = () => {
    setBulkEmailSubject('')
    setBulkEmailBody('')
    setSelectedBulkEmails([])
    setBulkEmailRecipients([])
    setBulkEmailModalVisible(true)
    gatherAllCustomerEmails()
  }

  const handleBulkEmailSelectAll = () => {
    setSelectedBulkEmails(bulkEmailRecipients.map(r => r.emailAddress))
  }

  const handleBulkEmailDeselectAll = () => {
    setSelectedBulkEmails([])
  }

  const handleBulkEmailToggle = emailAddress => {
    setSelectedBulkEmails(prev => {
      if (prev.includes(emailAddress)) {
        return prev.filter(e => e !== emailAddress)
      } else {
        return [...prev, emailAddress]
      }
    })
  }

  const handleSendBulkEmail = async () => {
    if (!supplierId) {
      message.error('Missing supplier information')
      return
    }

    if (!bulkEmailSubject.trim() || !bulkEmailBody.trim()) {
      message.error('Subject and email body are required')
      return
    }

    const selectedRecipients = bulkEmailRecipients.filter(r =>
      selectedBulkEmails.includes(r.emailAddress),
    )

    if (selectedRecipients.length === 0) {
      message.error('Please select at least one email address')
      return
    }

    if (selectedRecipients.length > 200) {
      message.error(
        'Maximum 200 recipients allowed. Please deselect some emails.',
      )
      return
    }

    setBulkEmailSending(true)
    try {
      const response = await API.post(
        'api2',
        'supplier/marketing/email/bulk-email',
        {
          body: {
            supplierId: supplierId,
            subject: bulkEmailSubject.trim(),
            emailBody: bulkEmailBody.trim(),
            recipients: selectedRecipients.map(r => ({
              emailAddress: r.emailAddress,
              venueIds: r.venueIds,
            })),
          },
        },
      )

      if (response.success) {
        message.success(
          `Email sent successfully to ${
            response.totalSent || selectedRecipients.length
          } recipients!`,
        )
        setBulkEmailModalVisible(false)
        setBulkEmailSubject('')
        setBulkEmailBody('')
        setSelectedBulkEmails([])
        setBulkEmailRecipients([])
      } else {
        message.error(response.error || 'Failed to send bulk email')
      }
    } catch (error) {
      console.error('Error sending bulk email:', error)
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to send bulk email. Please try again.'
      message.error(errorMessage)
    } finally {
      setBulkEmailSending(false)
    }
  }

  const handleBroadcastMessageClick = () => {
    setBroadcastMessageText('')
    setSelectedBroadcastVenues([])
    setBroadcastSearchText('')
    setBroadcastMessageModalVisible(true)
  }

  // Filter venues for broadcast message modal based on search
  const filteredBroadcastVenues = useMemo(() => {
    if (!broadcastSearchText.trim()) {
      return filteredCustomersWithStats
    }
    const searchTerm = broadcastSearchText.toLowerCase().trim()
    return filteredCustomersWithStats.filter(customer => {
      const venueName = (customer.venueTitle || '').toLowerCase()
      const accountNumber = (customer.accountNumber || '').toLowerCase()
      return (
        venueName.includes(searchTerm) || accountNumber.includes(searchTerm)
      )
    })
  }, [filteredCustomersWithStats, broadcastSearchText])

  const handleBroadcastSelectAll = () => {
    setSelectedBroadcastVenues(
      filteredBroadcastVenues.map(c => c._id || c.venueId).filter(Boolean),
    )
  }

  const handleBroadcastDeselectAll = () => {
    setSelectedBroadcastVenues([])
  }

  const handleBroadcastToggle = venueId => {
    setSelectedBroadcastVenues(prev => {
      if (prev.includes(venueId)) {
        return prev.filter(id => id !== venueId)
      } else {
        return [...prev, venueId]
      }
    })
  }

  const handleSendBroadcastMessage = async () => {
    if (!supplierId || !supplierTitle) {
      message.error('Missing supplier information')
      return
    }

    if (!broadcastMessageText.trim()) {
      message.error('Message text is required')
      return
    }

    if (selectedBroadcastVenues.length === 0) {
      message.error('Please select at least one venue')
      return
    }

    if (selectedBroadcastVenues.length > 200) {
      message.error('Maximum 200 venues allowed. Please deselect some venues.')
      return
    }

    // Close selection modal and open progress modal
    setBroadcastMessageModalVisible(false)
    setBroadcastProgress({
      visible: true,
      current: 0,
      total: selectedBroadcastVenues.length,
      success: 0,
      failed: 0,
      completed: false,
    })

    setBroadcastSending(true)

    try {
      // First, create the broadcast record in the backend
      const createResponse = await API.post(
        'api2',
        'supplier/marketing/message/broadcast',
        {
          body: {
            supplierId: supplierId,
            venueIds: selectedBroadcastVenues,
            messageText: broadcastMessageText.trim(),
          },
        },
      )

      const messageId = createResponse.messageId

      // Wait for TalkJS to be ready
      await Talk.ready

      // Create or reuse TalkJS session
      const me = new Talk.User({
        id: supplierId,
        name: supplierTitle,
        welcomeMessage: 'Hey there! How are you? :-)',
        role: 'default',
      })

      if (!window.talkSession) {
        window.talkSession = new Talk.Session({
          appId: 'GR4DzdvG',
          me: me,
        })
      } else {
        window.talkSession.me = me
      }

      let successCount = 0
      let failureCount = 0

      // Get venue data for all selected venues
      const venuesData = filteredCustomersWithStats.filter(customer => {
        const venueId = customer._id || customer.venueId
        return selectedBroadcastVenues.includes(venueId)
      })

      // Send message to each venue programmatically
      for (let i = 0; i < venuesData.length; i++) {
        const customer = venuesData[i]
        const venueId = customer._id || customer.venueId
        const venueTitle = customer.venueTitle || customer.title || 'Customer'

        try {
          // Update progress
          setBroadcastProgress(prev => ({
            ...prev,
            current: i + 1,
          }))

          // Create venue user object
          const other = new Talk.User({
            id: venueId,
            name: venueTitle,
            welcomeMessage: 'Hey there! Send us a message to start a chat 😁',
            role: 'venues',
          })

          // Get or create conversation
          const conversationId = Talk.oneOnOneId(me, other)
          const conversation =
            window.talkSession.getOrCreateConversation(conversationId)

          conversation.setParticipant(me)
          conversation.setParticipant(other)

          // CRITICAL: Initialize conversation by creating a hidden popup
          // TalkJS requires conversations to be "opened" (initialized) before
          // programmatic messages can be sent. This is why previously-opened
          // conversations work but new ones fail.
          const hiddenPopup = window.talkSession.createPopup(conversation, {
            keepOpen: true,
          })

          // Create a hidden container for the popup (off-screen, invisible)
          const hiddenContainer = document.createElement('div')
          hiddenContainer.style.position = 'fixed'
          hiddenContainer.style.left = '-9999px'
          hiddenContainer.style.top = '-9999px'
          hiddenContainer.style.width = '1px'
          hiddenContainer.style.height = '1px'
          hiddenContainer.style.overflow = 'hidden'
          hiddenContainer.style.opacity = '0'
          hiddenContainer.style.pointerEvents = 'none'
          document.body.appendChild(hiddenContainer)

          try {
            // Mount the popup to initialize the conversation on TalkJS servers
            await hiddenPopup.mount({ container: hiddenContainer, show: false })

            // Select the conversation to ensure it's active/initialized
            hiddenPopup.select(conversation)

            // Wait for TalkJS to sync/initialize the conversation
            // This is critical - without this wait, the conversation may not be ready
            await new Promise(resolve => setTimeout(resolve, 400))

            // Now send the message - conversation is now initialized
            await conversation.sendMessage(broadcastMessageText.trim())
          } finally {
            // Clean up: destroy popup and remove container
            try {
              hiddenPopup.destroy()
            } catch (e) {
              console.warn(`Error destroying popup for venue ${venueId}:`, e)
            }
            try {
              if (document.body.contains(hiddenContainer)) {
                document.body.removeChild(hiddenContainer)
              }
            } catch (e) {
              console.warn(`Error removing container for venue ${venueId}:`, e)
            }
          }

          // Trigger notification (same as 1:1 messages)
          try {
            await supplierNotifications(venueId, supplierId)
          } catch (notifError) {
            console.error(
              `Error sending notification for venue ${venueId}:`,
              notifError,
            )
            // Don't fail the message send if notification fails
          }

          successCount++
          setBroadcastProgress(prev => ({
            ...prev,
            success: successCount,
          }))
        } catch (error) {
          console.error(`Error sending message to venue ${venueId}:`, error)
          failureCount++
          setBroadcastProgress(prev => ({
            ...prev,
            failed: failureCount,
          }))
        }

        // Small delay to avoid rate limiting (increased slightly due to initialization overhead)
        if (i < venuesData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200))
        }
      }

      // Update the message record with actual notification count
      if (messageId) {
        try {
          await API.patch('api2', `supplier/marketing/messages/${messageId}`, {
            body: {
              notificationSentCount: successCount,
            },
          })
        } catch (updateError) {
          console.error(
            'Error updating message notification count:',
            updateError,
          )
          // Don't fail the whole operation if update fails
        }
      }

      // Update progress to completed
      setBroadcastProgress(prev => ({
        ...prev,
        current: prev.total,
        completed: true,
      }))

      if (failureCount > 0) {
        message.warning(
          `Broadcast sent: ${successCount} successful, ${failureCount} failed`,
        )
      } else {
        message.success(
          `Broadcast sent successfully to ${successCount} venues!`,
        )
      }
    } catch (error) {
      console.error('Error sending broadcast message:', error)
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        'Failed to send broadcast message. Please try again.'
      message.error(errorMessage)
      setBroadcastProgress(prev => ({
        ...prev,
        completed: true,
        failed: prev.total - prev.success,
      }))
    } finally {
      setBroadcastSending(false)
    }
  }

  const formatDate = dateValue => {
    if (!dateValue) return '—'
    const parsed = moment(dateValue)
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—'
  }

  const toTimestamp = dateValue => {
    const parsed = moment(dateValue)
    return parsed.isValid() ? parsed.valueOf() : 0
  }

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      // eslint-disable-next-line no-unused-vars
      render: (_, record) => (
        <ActionsContainer>
          <ActionButton
            type='link'
            icon={<MessageOutlined />}
            onClick={() => handleMessageClick(record)}
            size='small'
            aria-label={`Message ${record.venueTitle || 'customer'}`}
          >
            Message
          </ActionButton>
          {record.emailAddress ? (
            <ActionButton
              type='link'
              icon={<MailOutlined />}
              onClick={() => handleEmailClick(record)}
              size='small'
              aria-label={`Email ${record.venueTitle || 'customer'}`}
            >
              Email
            </ActionButton>
          ) : (
            <Tooltip title='No email address available'>
              <ActionButton
                type='link'
                icon={<MailOutlined />}
                disabled
                size='small'
                aria-label={`Email ${
                  record.venueTitle || 'customer'
                } (unavailable)`}
              >
                Email
              </ActionButton>
            </Tooltip>
          )}
        </ActionsContainer>
      ),
    },
    {
      title: 'Venue Title',
      dataIndex: 'venueTitle',
      key: 'venueTitle',
      sorter: (a, b) => (a.venueTitle || '').localeCompare(b.venueTitle || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      sorter: (a, b) =>
        (a.accountNumber || '').localeCompare(b.accountNumber || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Email Address',
      dataIndex: 'emailAddress',
      key: 'emailAddress',
      render: email => email || '—',
    },
    {
      title: 'Last Order',
      key: 'lastOrder',
      // eslint-disable-next-line no-unused-vars
      render: (_, record) => formatDate(getLastOrderDate(record)),
      sorter: (a, b) => {
        const dateA = toTimestamp(getLastOrderDate(a))
        const dateB = toTimestamp(getLastOrderDate(b))
        return dateA - dateB
      },
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Onboarded Date',
      key: 'onBoarded',
      // eslint-disable-next-line no-unused-vars
      render: (_, record) => formatDate(getOnboardedDate(record)),
      sorter: (a, b) => {
        const dateA = toTimestamp(getOnboardedDate(a))
        const dateB = toTimestamp(getOnboardedDate(b))
        return dateA - dateB
      },
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Status',
      key: 'status',
      // eslint-disable-next-line no-unused-vars
      render: (_, record) => {
        const meta = getCustomerStatusMeta(record)
        return (
          <Tag color={meta.color} style={{ margin: 0 }}>
            {meta.label}
          </Tag>
        )
      },
      sorter: (a, b) => {
        const statusPriority = { active: 3, recent: 2, inactive: 1, unknown: 0 }
        return (
          (statusPriority[getCustomerStatusValue(a)] || 0) -
          (statusPriority[getCustomerStatusValue(b)] || 0)
        )
      },
      sortDirections: ['descend', 'ascend'],
    },
  ]

  return (
    <TabWrapper>
      <InviteBanner>
        <BannerCopy>
          <BannerTitle>Reach more customers instantly.</BannerTitle>
          <BannerBody>
            Add more venues to unlock powerful messaging and communication tools
            inside OMS.
          </BannerBody>
        </BannerCopy>
        <BannerCTA type='primary' onClick={navigateToCustomers}>
          Invite Customers
        </BannerCTA>
      </InviteBanner>
      <ControlsContainer>
        <FiltersContainer>
          <FilterButton
            $active={filters.active}
            onClick={() => handleFilterToggle('active')}
          >
            Active
          </FilterButton>
          <FilterButton
            $active={filters.inactive}
            onClick={() => handleFilterToggle('inactive')}
          >
            Inactive
          </FilterButton>
          <FilterButton
            $active={filters.recentlyJoined}
            onClick={() => handleFilterToggle('recentlyJoined')}
          >
            Recently Joined
          </FilterButton>
        </FiltersContainer>
        <SearchContainer>
          <Input
            placeholder='Search by venue name or account number...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
            style={{ maxWidth: '400px' }}
          />
        </SearchContainer>
      </ControlsContainer>

      <BroadcastActions>
        <Button
          type='primary'
          icon={<MessageOutlined />}
          onClick={handleBroadcastMessageClick}
        >
          Message All Customers
        </Button>
        <Button
          type='primary'
          icon={<MailOutlined />}
          onClick={handleBulkEmailClick}
        >
          Email All Customers
        </Button>
      </BroadcastActions>

      <TableContainer>
        {customersLoading ? (
          <SkeletonWrapper>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton
                key={`skeleton-row-${index}`}
                active
                title={false}
                paragraph={{ rows: 1, width: '100%' }}
              />
            ))}
          </SkeletonWrapper>
        ) : showNoCustomersEmptyState ? (
          <EmptyStateWrapper>
            <EmptyIcon>
              <UserAddOutlined />
            </EmptyIcon>
            <EmptyTitle>You haven&apos;t added any customers yet.</EmptyTitle>
            <EmptyDescription>
              Add customers to start messaging, emailing and engaging with them
              directly on OMS.
            </EmptyDescription>
            <EmptyActions>
              <Button type='primary' onClick={navigateToCustomers}>
                Invite Customers
              </Button>
            </EmptyActions>
          </EmptyStateWrapper>
        ) : showSearchEmptyState ? (
          <EmptyStateWrapper>
            <EmptyIcon>
              <SearchOutlined />
            </EmptyIcon>
            <EmptyTitle>No matching customers found</EmptyTitle>
            <EmptyDescription>
              Try adjusting your search or filters to see more results.
            </EmptyDescription>
          </EmptyStateWrapper>
        ) : showFilterEmptyState ? (
          <EmptyStateWrapper>
            <EmptyIcon>
              <FilterOutlined />
            </EmptyIcon>
            <EmptyTitle>No customers match your filters</EmptyTitle>
            <EmptyDescription>
              Clear your filters to see all customers.
            </EmptyDescription>
            <EmptyActions>
              <Button onClick={handleClearFilters}>Clear Filters</Button>
            </EmptyActions>
          </EmptyStateWrapper>
        ) : showDefaultEmptyState ? (
          <EmptyStateWrapper>
            <EmptyIcon>
              <FilterOutlined />
            </EmptyIcon>
            <EmptyTitle>No customers to display</EmptyTitle>
            <EmptyDescription>
              Adjust your filters or add customers to see them here.
            </EmptyDescription>
            <EmptyActions>
              <Button onClick={handleClearFilters} disabled={!anyFiltersActive}>
                Clear Filters
              </Button>
            </EmptyActions>
          </EmptyStateWrapper>
        ) : filteredCustomersWithStats.length > 0 ? (
          <Table
            columns={columns}
            dataSource={filteredCustomersWithStats}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowKey={record => record._id || record.key}
          />
        ) : null}
      </TableContainer>

      {/* Email Modal */}
      <Modal
        title='Email Customer'
        visible={emailModalVisible}
        onCancel={() => {
          if (!emailSending) {
            setEmailModalVisible(false)
            setSelectedCustomer(null)
            setEmailSubject('')
            setEmailBody('')
            setSelectedEmail(null)
          }
        }}
        keyboard={true}
        maskClosable={!emailSending}
        footer={[
          <Button
            key='close'
            onClick={() => {
              setEmailModalVisible(false)
              setSelectedCustomer(null)
              setEmailSubject('')
              setEmailBody('')
              setSelectedEmail(null)
            }}
            disabled={emailSending}
          >
            Close
          </Button>,
          <Button
            key='send'
            type='primary'
            onClick={handleSendEmail}
            loading={emailSending}
            disabled={
              emailSending ||
              !emailSubject.trim() ||
              !emailBody.trim() ||
              !selectedEmail
            }
          >
            Send
          </Button>,
        ]}
        footerStyle={{
          gap: '16px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <ModalContent>
          <ModalDescription>
            {selectedCustomer
              ? `Send an email to ${selectedCustomer.venueTitle}.`
              : 'Select a customer to begin.'}
          </ModalDescription>
          {venueUsers.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label
                style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}
              >
                Select recipient email:
              </label>
              <Select
                style={{ width: '100%' }}
                value={selectedEmail}
                onChange={setSelectedEmail}
                disabled={emailSending || venueUsersLoading}
                loading={venueUsersLoading}
                placeholder='Select email address'
              >
                {venueUsers.map((user, index) => (
                  <Select.Option key={user.email || index} value={user.email}>
                    {user.email}{' '}
                    {user.isPrimary ? '(Primary owner)' : `(${user.name})`}
                  </Select.Option>
                ))}
              </Select>
            </div>
          )}
          <Input
            placeholder='Subject'
            value={emailSubject}
            onChange={e => setEmailSubject(e.target.value)}
            disabled={emailSending}
          />
          <Input.TextArea
            rows={5}
            placeholder='Email body...'
            value={emailBody}
            onChange={e => setEmailBody(e.target.value)}
            disabled={emailSending}
          />
        </ModalContent>
      </Modal>

      {/* Broadcast Message Modal */}
      <Modal
        title='Message All Customers'
        visible={broadcastMessageModalVisible}
        onCancel={() => {
          if (!broadcastSending) {
            setBroadcastMessageModalVisible(false)
            setBroadcastMessageText('')
            setSelectedBroadcastVenues([])
            setBroadcastSearchText('')
          }
        }}
        width={800}
        keyboard={true}
        maskClosable={!broadcastSending}
        footer={[
          <Button
            key='close'
            onClick={() => {
              setBroadcastMessageModalVisible(false)
              setBroadcastMessageText('')
              setSelectedBroadcastVenues([])
              setBroadcastSearchText('')
            }}
            disabled={broadcastSending}
          >
            Close
          </Button>,
          <Button
            key='send'
            type='primary'
            onClick={handleSendBroadcastMessage}
            loading={broadcastSending}
            disabled={
              broadcastSending ||
              !broadcastMessageText.trim() ||
              selectedBroadcastVenues.length === 0 ||
              selectedBroadcastVenues.length > 200
            }
          >
            Send Message
          </Button>,
        ]}
        footerStyle={{
          gap: '16px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <ModalContent>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <ModalDescription style={{ margin: 0 }}>
              {selectedBroadcastVenues.length > 0
                ? `${selectedBroadcastVenues.length} venue${
                    selectedBroadcastVenues.length !== 1 ? 's' : ''
                  } selected · ${filteredBroadcastVenues.length} ${
                    broadcastSearchText.trim() ? 'filtered' : 'total'
                  } venue${
                    filteredBroadcastVenues.length !== 1 ? 's' : ''
                  } available`
                : `${filteredBroadcastVenues.length} ${
                    broadcastSearchText.trim() ? 'filtered' : 'total'
                  } venue${
                    filteredBroadcastVenues.length !== 1 ? 's' : ''
                  } available`}
            </ModalDescription>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size='small'
                onClick={handleBroadcastSelectAll}
                disabled={
                  filteredBroadcastVenues.length === 0 || broadcastSending
                }
              >
                Select All
              </Button>
              <Button
                size='small'
                onClick={handleBroadcastDeselectAll}
                disabled={
                  selectedBroadcastVenues.length === 0 || broadcastSending
                }
              >
                Deselect All
              </Button>
            </div>
          </div>

          <Input
            placeholder='Search venues by name or account number...'
            prefix={<SearchOutlined />}
            value={broadcastSearchText}
            onChange={e => setBroadcastSearchText(e.target.value)}
            allowClear
            disabled={broadcastSending}
            style={{ marginBottom: 16 }}
          />

          {selectedBroadcastVenues.length > 200 && (
            <div
              style={{
                padding: 12,
                marginBottom: 16,
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: 4,
                color: '#991b1b',
              }}
            >
              <strong>Warning:</strong> You&apos;ve selected{' '}
              {selectedBroadcastVenues.length} venues. Please reduce to 200 or
              fewer.
            </div>
          )}

          <div
            style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              padding: 12,
              marginBottom: 16,
              backgroundColor: '#f8fafc',
            }}
          >
            {filteredBroadcastVenues.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#64748b',
                }}
              >
                {broadcastSearchText.trim()
                  ? 'No venues match your search.'
                  : 'No venues available.'}
              </div>
            ) : (
              filteredBroadcastVenues.map((customer, index) => {
                const venueId = customer._id || customer.venueId
                const isSelected = selectedBroadcastVenues.includes(venueId)

                return (
                  <div
                    key={venueId || index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom:
                        index < filteredCustomersWithStats.length - 1
                          ? '1px solid #e2e8f0'
                          : 'none',
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleBroadcastToggle(venueId)}
                      disabled={broadcastSending}
                    />
                    <div style={{ marginLeft: 12, flex: 1 }}>
                      <div style={{ fontWeight: 500, color: '#1e293b' }}>
                        {customer.venueTitle || 'Unknown Venue'}
                      </div>
                      {customer.accountNumber && (
                        <div
                          style={{
                            fontSize: 12,
                            color: '#64748b',
                            marginTop: 2,
                          }}
                        >
                          Account: {customer.accountNumber}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <Input.TextArea
            rows={5}
            placeholder='Type your message...'
            value={broadcastMessageText}
            onChange={e => setBroadcastMessageText(e.target.value)}
            disabled={broadcastSending}
            maxLength={5000}
            showCount
          />
        </ModalContent>
      </Modal>

      {/* Broadcast Progress Modal */}
      <Modal
        title={
          broadcastProgress.completed
            ? 'Broadcast Complete'
            : 'Sending Messages...'
        }
        visible={broadcastProgress.visible}
        onCancel={() => {
          if (broadcastProgress.completed) {
            setBroadcastProgress({
              visible: false,
              current: 0,
              total: 0,
              success: 0,
              failed: 0,
              completed: false,
            })
            setBroadcastMessageText('')
            setSelectedBroadcastVenues([])
            setBroadcastSearchText('')
          }
        }}
        keyboard={broadcastProgress.completed}
        maskClosable={broadcastProgress.completed}
        footer={[
          <Button
            key='close'
            onClick={() => {
              setBroadcastProgress({
                visible: false,
                current: 0,
                total: 0,
                success: 0,
                failed: 0,
                completed: false,
              })
              setBroadcastMessageText('')
              setSelectedBroadcastVenues([])
            }}
            disabled={!broadcastProgress.completed}
          >
            {broadcastProgress.completed ? 'Close' : 'Sending...'}
          </Button>,
        ]}
        closable={broadcastProgress.completed}
      >
        <ModalContent>
          {!broadcastProgress.completed ? (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Spin size='large' />
              </div>
              <Progress
                percent={
                  broadcastProgress.total > 0
                    ? Math.round(
                        (broadcastProgress.current / broadcastProgress.total) *
                          100,
                      )
                    : 0
                }
                status='active'
                style={{ marginBottom: 16 }}
              />
              <ModalDescription
                style={{ textAlign: 'center', marginBottom: 8 }}
              >
                Sending {broadcastProgress.current} of {broadcastProgress.total}
                ...
              </ModalDescription>
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>
                  {broadcastProgress.failed === 0 ? '✅' : '⚠️'}
                </div>
                <ModalDescription style={{ marginBottom: 16 }}>
                  {broadcastProgress.failed === 0
                    ? `Successfully sent to ${broadcastProgress.success} venue${
                        broadcastProgress.success !== 1 ? 's' : ''
                      }!`
                    : `Sent to ${broadcastProgress.success} venue${
                        broadcastProgress.success !== 1 ? 's' : ''
                      }, ${broadcastProgress.failed} failed.`}
                </ModalDescription>
                <Progress
                  percent={100}
                  status={
                    broadcastProgress.failed === 0 ? 'success' : 'exception'
                  }
                  format={() =>
                    `${broadcastProgress.success} / ${broadcastProgress.total}`
                  }
                />
              </div>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Bulk Email Modal */}
      <Modal
        title='Email All Customers'
        visible={bulkEmailModalVisible}
        onCancel={() => {
          if (!bulkEmailSending) {
            setBulkEmailModalVisible(false)
            setBulkEmailSubject('')
            setBulkEmailBody('')
            setSelectedBulkEmails([])
            setBulkEmailRecipients([])
          }
        }}
        width={800}
        keyboard={true}
        maskClosable={!bulkEmailSending}
        footer={[
          <Button
            key='close'
            onClick={() => {
              setBulkEmailModalVisible(false)
              setBulkEmailSubject('')
              setBulkEmailBody('')
              setSelectedBulkEmails([])
              setBulkEmailRecipients([])
            }}
            disabled={bulkEmailSending}
          >
            Close
          </Button>,
          <Button
            key='send'
            type='primary'
            onClick={handleSendBulkEmail}
            loading={bulkEmailSending}
            disabled={
              bulkEmailSending ||
              !bulkEmailSubject.trim() ||
              !bulkEmailBody.trim() ||
              selectedBulkEmails.length === 0 ||
              selectedBulkEmails.length > 200
            }
          >
            Send Email
          </Button>,
        ]}
        footerStyle={{
          gap: '16px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <ModalContent>
          {bulkEmailRecipientsLoading ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 20,
              }}
            >
              <Spin size='large' />
              <div style={{ maxWidth: 400 }}>
                <p
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: '#1e293b',
                    marginBottom: 8,
                  }}
                >
                  🕵️ Gathering all your customer emails...
                </p>
                <p
                  style={{
                    fontSize: 14,
                    color: '#64748b',
                    lineHeight: 1.6,
                    margin: 0,
                  }}
                >
                  This takes a moment while we find all of your venues&apos;
                  email addresses. Bear with us while we do that! 🚀
                </p>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <ModalDescription style={{ margin: 0 }}>
                  {selectedBulkEmails.length > 0
                    ? `${selectedBulkEmails.length} email${
                        selectedBulkEmails.length !== 1 ? 's' : ''
                      } selected · ${
                        bulkEmailRecipients.length
                      } total unique email${
                        bulkEmailRecipients.length !== 1 ? 's' : ''
                      } found`
                    : `${bulkEmailRecipients.length} unique email${
                        bulkEmailRecipients.length !== 1 ? 's' : ''
                      } found`}
                </ModalDescription>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button
                    size='small'
                    onClick={handleBulkEmailSelectAll}
                    disabled={
                      bulkEmailRecipients.length === 0 || bulkEmailSending
                    }
                  >
                    Select All
                  </Button>
                  <Button
                    size='small'
                    onClick={handleBulkEmailDeselectAll}
                    disabled={
                      selectedBulkEmails.length === 0 || bulkEmailSending
                    }
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              {selectedBulkEmails.length > 200 && (
                <div
                  style={{
                    padding: 12,
                    marginBottom: 16,
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: 4,
                    color: '#991b1b',
                  }}
                >
                  <strong>Warning:</strong> You&apos;ve selected{' '}
                  {selectedBulkEmails.length} emails. Please reduce to 200 or
                  fewer.
                </div>
              )}

              <div
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: 4,
                  padding: 12,
                  marginBottom: 16,
                  backgroundColor: '#f8fafc',
                }}
              >
                {bulkEmailRecipients.length === 0 ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#64748b',
                    }}
                  >
                    No email addresses found for customers.
                  </div>
                ) : (
                  bulkEmailRecipients.map((recipient, index) => {
                    const isSelected = selectedBulkEmails.includes(
                      recipient.emailAddress,
                    )
                    const venueNamesText =
                      recipient.venueNames.length === 1
                        ? recipient.venueNames[0].venueName
                        : recipient.venueNames.length > 1
                        ? `${recipient.venueNames[0].venueName} + ${
                            recipient.venueNames.length - 1
                          } more`
                        : 'Unknown Venue'

                    return (
                      <div
                        key={recipient.emailAddress || index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 0',
                          borderBottom:
                            index < bulkEmailRecipients.length - 1
                              ? '1px solid #e2e8f0'
                              : 'none',
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() =>
                            handleBulkEmailToggle(recipient.emailAddress)
                          }
                          disabled={bulkEmailSending}
                        />
                        <div style={{ marginLeft: 12, flex: 1 }}>
                          <div style={{ fontWeight: 500, color: '#1e293b' }}>
                            {recipient.emailAddress}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: '#64748b',
                              marginTop: 2,
                            }}
                          >
                            {venueNamesText}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              <Input
                placeholder='Subject'
                value={bulkEmailSubject}
                onChange={e => setBulkEmailSubject(e.target.value)}
                disabled={bulkEmailSending}
                style={{ marginBottom: 16 }}
              />
              <Input.TextArea
                rows={5}
                placeholder='Email body...'
                value={bulkEmailBody}
                onChange={e => setBulkEmailBody(e.target.value)}
                disabled={bulkEmailSending}
              />
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Progress Modal */}
      <Modal
        title='Sending...'
        visible={progressModalVisible}
        onCancel={() => setProgressModalVisible(false)}
        footer={null}
        keyboard={true}
        maskClosable={true}
      >
        <ModalContent>
          <ProgressBar>
            <ProgressFill />
          </ProgressBar>
          <ModalDescription style={{ textAlign: 'center' }}>
            This is a preview. Real progress will be added in Phase 3.
          </ModalDescription>
        </ModalContent>
      </Modal>
    </TabWrapper>
  )
}

const TabWrapper = styled.div`
  --sp-color-surface: ${supplierPortalTheme.colors.surface};
  --sp-color-text: ${supplierPortalTheme.colors.text};
  --sp-color-muted: ${supplierPortalTheme.colors.muted};
  --sp-color-border: ${supplierPortalTheme.colors.border};
  --sp-color-primary: ${supplierPortalTheme.colors.primary};
  --sp-radius-card: ${supplierPortalTheme.radii.card};
  --sp-radius-control: ${supplierPortalTheme.radii.control};
  --sp-shadow-card: ${supplierPortalTheme.shadows.card};
  --sp-space-xs: ${supplierPortalTheme.spacing.xs};
  --sp-space-sm: ${supplierPortalTheme.spacing.sm};
  --sp-space-md: ${supplierPortalTheme.spacing.md};
  --sp-space-lg: ${supplierPortalTheme.spacing.lg};
  --sp-space-xl: ${supplierPortalTheme.spacing.xl};
  width: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-lg);
`

const InviteBanner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-space-md);
  padding: var(--sp-space-lg);
  border-radius: var(--sp-radius-card);
  background: rgba(165, 214, 63, 0.15);
  border: 1px solid rgba(165, 214, 63, 0.4);
`

const BannerCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-xs);
`

const BannerTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #1f2937;
`

const BannerBody = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 14px;
`

const BannerCTA = styled(Button)`
  && {
    background: #a5d63f;
    border-color: #a5d63f;
    color: #0f172a;
    font-weight: 600;
    &:hover,
    &:focus {
      background: #9ac835;
      border-color: #9ac835;
      color: #0f172a;
    }
  }
`

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-space-md);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const FiltersContainer = styled.div`
  display: flex;
  gap: var(--sp-space-sm);
  flex-wrap: wrap;
`

const FilterButton = styled.button`
  padding: var(--sp-space-xs) var(--sp-space-md);
  border-radius: var(--sp-radius-control);
  border: 1px solid var(--sp-color-border);
  background: ${props =>
    props.$active ? 'var(--sp-color-primary)' : 'var(--sp-color-surface)'};
  color: ${props =>
    props.$active ? 'var(--sp-color-surface)' : 'var(--sp-color-text)'};
  font-weight: ${props => (props.$active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    border-color: var(--sp-color-primary);
    background: ${props =>
      props.$active ? 'var(--sp-color-primary)' : 'rgba(14, 165, 233, 0.08)'};
  }

  &:active {
    transform: scale(0.98);
  }
`

const SearchContainer = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 400px;
`

const BroadcastActions = styled.div`
  display: flex;
  gap: var(--sp-space-md);
  flex-wrap: wrap;
  align-items: center;
`

const TableContainer = styled.div`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);
  overflow: hidden;
  padding: var(--sp-space-lg);

  .ant-table-thead > tr > th {
    background: #f7fafc;
    border-bottom: 1px solid var(--sp-color-border);
    font-weight: 600;
    color: var(--sp-color-text);
    padding: var(--sp-space-md);
  }

  .ant-table {
    border: none;
  }

  .ant-table-tbody > tr {
    height: 48px;

    &:hover {
      background: #f7fafc;

      > td {
        background: #f7fafc !important;
      }
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--sp-color-border);
    padding: var(--sp-space-md);
  }
`

const ActionsContainer = styled.div`
  display: flex;
  gap: var(--sp-space-xs);
  align-items: center;
`

const ActionButton = styled(Button)`
  && {
    font-weight: 600;
    color: var(--sp-color-primary);

    &:hover {
      color: var(--sp-color-primary);
      opacity: 0.8;
    }

    &:disabled {
      color: var(--sp-color-muted);
      opacity: 0.5;
    }
  }
`

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-sm);
`

const EmptyStateWrapper = styled.div`
  padding: var(--sp-space-xl);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-space-sm);
  min-height: 320px;
  justify-content: center;
`

const EmptyIcon = styled.div`
  font-size: 48px;
  color: var(--sp-color-muted);
`

const EmptyTitle = styled.h3`
  margin: 0;
  color: var(--sp-color-text);
`

const EmptyDescription = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
`

const EmptyActions = styled.div`
  margin-top: var(--sp-space-sm);
  display: flex;
  gap: var(--sp-space-xs);
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-md);
  padding: var(--sp-space-xs) 0;
`

const ModalDescription = styled.p`
  margin: 0;
  color: var(--sp-color-text);
`

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  border-radius: var(--sp-radius-control);
  background: rgba(148, 163, 184, 0.2);
  overflow: hidden;
`

const ProgressFill = styled.div`
  width: 30%;
  height: 100%;
  background: var(--sp-color-primary);
  opacity: 0.5;
`

export default CommunicateTab
